import { useState, useEffect } from 'react';

/**
 * Custom hook to prevent hydration mismatches
 * Returns true only after the component has mounted on the client side
 * 
 * @returns {boolean} - true if component is hydrated, false during SSR
 */
export const useHydration = () => {
    const [isHydrated, setIsHydrated] = useState(false);

    useEffect(() => {
        setIsHydrated(true);
    }, []);

    return isHydrated;
};

/**
 * Custom hook for safe client-side only operations
 * Prevents hydration mismatches by ensuring code only runs on client
 * 
 * @param {Function} callback - Function to run on client side
 * @param {Array} deps - Dependencies array for useEffect
 */
export const useClientEffect = (callback, deps = []) => {
    const isHydrated = useHydration();

    useEffect(() => {
        if (isHydrated) {
            callback();
        }
    }, [isHydrated, ...deps]);
};

/**
 * Safe wrapper for window object access
 * Returns undefined during SSR to prevent hydration mismatches
 * 
 * @param {string} property - Property to access on window
 * @returns {any} - Value of window property or undefined
 */
export const safeWindow = (property) => {
    if (typeof window === 'undefined') return undefined;
    return window[property];
};

/**
 * Safe wrapper for localStorage access
 * Returns null during SSR to prevent hydration mismatches
 * 
 * @param {string} key - localStorage key
 * @returns {string|null} - Value from localStorage or null
 */
export const safeLocalStorage = (key) => {
    if (typeof window === 'undefined') return null;
    try {
        return localStorage.getItem(key);
    } catch (error) {
        console.warn('localStorage access failed:', error);
        return null;
    }
};

/**
 * Safe wrapper for document object access
 * Returns undefined during SSR to prevent hydration mismatches
 * 
 * @param {string} property - Property to access on document
 * @returns {any} - Value of document property or undefined
 */
export const safeDocument = (property) => {
    if (typeof document === 'undefined') return undefined;
    return document[property];
};
